/**
 * Phoenix Potion — Plan 4a Task 6.1
 *
 * Self-cast potion that grants +30 HP as real overheal.
 * - Target: self (self-cast only)
 * - Heal: adds 30 to player.hp at apply time (can exceed 30 cap)
 * - Duration: none; overheal is consumed only by damage
 * - Stone Skin block: NOT blocked (targetType='self', Stone Skin rule 8)
 * - Cannot revive dead players: if caster isDead=true, cast returns {success: false}
 *
 * Self-registers via addItem(phoenixPotion) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import { appendEvent } from '../event-log';
import { addItem } from './registry';
import { applyHeal } from '../healing';

export const phoenixPotion: Item = {
  id: 'phoenix_potion',
  name: 'Phoenix Potion',
  description: 'Restores 30 HP as a stackable overheal. Cannot revive dead players.',
  targetType: 'self',
  category: 'simple',
  rarity: 'uncommon',
  blockedByStoneSkin: false,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId } = ctx;

    // Step 1: Validate caster exists
    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Phoenix Potion: caster not found: ${casterId}`);

    // Step 2: Check if caster is dead
    if (caster.isDead) {
      return { success: false };
    }

    // Step 3: Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'phoenix_potion',
    });

    // Step 4: Apply real HP overheal. The excess HP is not a status effect and does not decay.
    applyHeal(state, casterId, 30, 'item_phoenix_potion', { allowOverheal: true });

    return { success: true };
  },
};

// Self-register the item
addItem(phoenixPotion);
